<template>
  <div>
    <template v-for="item in list">
      <component
        :is="item.component || 'BTooltipButton'"
        :key="item.key"
        :$key="item.key"
        :icon="item.icon"
        :tooltip="item.tooltip"
        :disabled="disabled"

        :commands="commands"
        :get-mark-attrs="getMarkAttrs"
        :get-node-attrs="getNodeAttrs"

        :icon-size="item.iconSize || '22px'"
        small
        depressed
        dense
        top
        :class-button="{
          'is-active': item.active,
          'is-text': item.text,
          'BEditorAction mr-1': true,
        }"
        @click="item.command()"
      />
    </template>
  </div>
</template>

<script>
import {
  createEditorModalLink,
  createEditorModalImage,
  createBEditorModalExcelCell,
  SETTINGS,
  SETTINGS_TABLE,
} from './utils'

import BTooltipButton from '@/components/common/BTooltipButton.vue'

const BEditorActionsColor = () => import(
  /* webpackChunkName: "BEditorActionsColor" */
  './BEditorActionsColor.vue'
)

const CREATE_TABLE_OPTIONS = {
  rowsCount: 3,
  colsCount: 3,
  withHeaderRow: false,
}

export default {
  name: 'BEditorActions',
  components: {
    BTooltipButton,
  },
  inheritAttrs: false,
  props: {
    editor: {
      type: Object,
      required: true,
    },
    disabled: Boolean,
    commands: {
      type: Object,
      required: true,
    },
    isActive: {
      type: Object,
      required: true,
    },
    getMarkAttrs: {
      type: Function,
      required: true,
    },
    getNodeAttrs: {
      type: Function,
      required: true,
    },
    codeViewMode: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    headingList() {
      const list = [1, 2, 3, 4, 5].map((level) => {
        const key = `H${level}`
        const options = { level }

        return {
          key,
          active: this.isActive.heading(options),
          command: () => this.handler('heading', options),
          icon: `mdi-format-header-${level}`,
        }
      })

      return list
    },
    tableList() {
      const active = this.isActive.table()
      const key = active ? 'deleteTable' : 'createTable'
      const [icon, tooltip] = SETTINGS_TABLE[key]

      const tableSetting = {
        key,
        command: () => this.handler(key, tableSetting, {}),
        icon,
        tooltip,
      }

      return active
        ? [tableSetting, ...this.tableHandleList]
        : [tableSetting]
    },
    tableHandleList() {
      const list = [
        'addColumnAfter',
        'deleteColumn',
        'addRowAfter',
        'deleteRow',
      ].map((key) => {
        const [icon, tooltip] = SETTINGS_TABLE[key]

        return {
          key,
          icon,
          tooltip,
          command: () => this.handler(key, void 0, {}),
        }
      })

      return list
    },
    alignmentList() {
      const attrs = this.getMarkAttrs('alignment') || this.getNodeAttrs('alignment')

      const list = ['left', 'center', 'right', 'justify'].map((align) => {
        const settings = SETTINGS.alignment[align]
        const active = attrs?.align === align
        const options = active ? { align: null } : { align }

        return {
          key: align,
          active,
          command: () => this.handler('alignment', options),
          icon: settings[0],
          iconSize: '18px',
          tooltip: settings[1],
        }
      })

      return list
    },
    colorList() {
      return {
        key: 'color',
        component: BEditorActionsColor,
      }
    },
    colorBgList() {
      return {
        key: 'color_bg',
        component: BEditorActionsColor,
      }
    },
    list() {
      const list = Object.keys(this.commands)
        .map((key) => {
          if (key === 'heading') return this.headingList
          if (key === 'createTable') return this.tableList
          if (key === 'alignment') return this.alignmentList
          if (key === 'color') return this.colorList
          if (key === 'color_bg') return this.colorBgList

          const settings = SETTINGS[key]
          if (!settings) return void 0

          const active = this.isActive[key]?.() || (key === 'code_view' && this.codeViewMode)
          const tooltip = active
            ? (settings[2] || settings[1])
            : settings[1]

          const options = {
            key,
            active,
            command: () => this.handler(key, void 0, options),
            icon: settings[0],
            tooltip,
          }

          return options
        })
        .filter(Boolean)
        .flat()

      return list
    },
  },
  methods: {
    handler(key, value, settings) {
      switch (key) {
        case 'link':
          return this.getLinkFromModal(settings)

        case 'image':
          return this.getImageFromModal(settings)

        case 'createTable':
          return this.commands[key](CREATE_TABLE_OPTIONS)

        case 'code_view': {
          const result = this.commands[key](this.codeViewMode)
          this.$emit('mode:toggle', result)
          return result
        }

        case 'excel_cell':
          return this.getExcelCellModal(settings)

        default:
          return this.commands[key](value)
      }
    },
    async getLinkFromModal(settings) {
      const attrs = this.getMarkAttrs('link')

      const hasSelection = (
        this.editor.selection.from
        !== this.editor.selection.to
      )

      const options = {
        active: settings.active,
        hasSelection,
        ...attrs,
      }

      const modal = await createEditorModalLink(this, options)
      const data = await modal.open()

      if (!options.active && !hasSelection && data?.text) {
        this.commands.insertLINK(data)
      } else if (data?.href) {
        this.commands.link(data)
      }

      this.editor.focus()
    },
    async getImageFromModal(settings) {
      const options = { active: settings.active }
      const modal = await createEditorModalImage(this, options)
      const data = await modal.open()

      if (data) {
        const { src, alt } = data
        this.commands.image({ src, alt })
      }

      this.editor.focus()
    },
    async getExcelCellModal(settings) {
      const attrs = this.getNodeAttrs('excel_cell')

      const options = {
        ...this.editor.extensions.options.excel_cell,
        active: settings.active,
        cell_uuid: attrs.cell_uuid,
      }

      const modal = await createBEditorModalExcelCell(this, options)
      const data = await modal.open()

      if (data) {
        data.cell_uuid.forEach((cell_uuid) => {
          this.commands.excel_cell({ cell_uuid })
        })
      }

      this.editor.focus()
    },
  },
}
</script>

<style lang="scss">
.BEditorAction {
  &.is-text i {
    font-size: 13px !important;
    font-style: normal;
    font-weight: bold;
  }

  &.is-active {
    background-color: $color-gray;
  }
}
</style>
